/**
 * Select options for rule condition values, keyed by ConditionField.
 * Used by the branch editor to show dropdowns where the evaluation model
 * uses a closed set; other fields stay free-text (numbers, dates, hours).
 */
import type { ConditionField, ConditionOperator } from '../types';
import { RESOURCE_FIELD_REGISTRY, TASK_CODE_OPTIONS as TASK_CODES } from '../types';

const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'True' },
  { value: 'false', label: 'False' },
];

/** FHIR R4 EpisodeOfCare.status */
const EPISODE_STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned' },
  { value: 'waitlist', label: 'Waitlist' },
  { value: 'active', label: 'Active' },
  { value: 'onhold', label: 'On hold' },
  { value: 'finished', label: 'Finished' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'entered-in-error', label: 'Entered in error' },
];

/** FHIR R4 ServiceRequest.status */
const SERVICE_REQUEST_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'on-hold', label: 'On hold' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'completed', label: 'Completed' },
  { value: 'entered-in-error', label: 'Entered in error' },
  { value: 'unknown', label: 'Unknown' },
];

/**
 * Intake / questionnaire-style statuses used in tests and typical flows.
 * Runtime may emit other strings; unknown values still evaluate via free-text rules.
 */
const INTAKE_STATUS_OPTIONS = [
  { value: 'completed', label: 'Completed' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'draft', label: 'Draft' },
  { value: 'amended', label: 'Amended' },
  { value: 'entered-in-error', label: 'Entered in error' },
  { value: 'stopped', label: 'Stopped' },
];

/** Therapy service types from the EoC PRD (therapy-service-type CodeSystem). */
const EPISODE_TYPE_OPTIONS = [
  { value: 'individual', label: 'Individual Therapy' },
  { value: 'couples', label: 'Couples Therapy' },
  { value: 'family', label: 'Family Therapy' },
  { value: 'group', label: 'Group Therapy' },
  { value: 'iop', label: 'Intensive Outpatient Program' },
  { value: 'medication-management', label: 'Medication Management' },
  { value: 'psych-testing', label: 'Psychological Testing' },
];

/** FHIR R4 Task.status subset relevant to the rules engine. */
const TASK_STATUS_OPTIONS = [
  { value: 'requested', label: 'Requested' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'rejected', label: 'Rejected' },
];

/** Task type codes used in the rules engine. */
const TASK_CODE_OPTIONS = TASK_CODES.map(({ value, label }) => ({ value, label }));

/** FHIR R4 Communication.status subset. */
const COMM_STATUS_OPTIONS = [
  { value: 'in-progress', label: 'In progress (unread)' },
  { value: 'completed', label: 'Completed (read)' },
  { value: 'not-done', label: 'Not done' },
];

/** Communication medium codes. */
const COMM_MEDIUM_OPTIONS = [
  { value: 'in-app', label: 'In-app' },
  { value: 'EMAILWRIT', label: 'Email' },
];

/** ActivityDefinition / Questionnaire publish status. */
const AD_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'retired', label: 'Retired' },
];

/** US state / territory postal codes for patient.address.state */
const US_STATE_OPTIONS: { value: string; label: string }[] = [
  ['AL', 'Alabama'],
  ['AK', 'Alaska'],
  ['AZ', 'Arizona'],
  ['AR', 'Arkansas'],
  ['CA', 'California'],
  ['CO', 'Colorado'],
  ['CT', 'Connecticut'],
  ['DE', 'Delaware'],
  ['DC', 'District of Columbia'],
  ['FL', 'Florida'],
  ['GA', 'Georgia'],
  ['HI', 'Hawaii'],
  ['ID', 'Idaho'],
  ['IL', 'Illinois'],
  ['IN', 'Indiana'],
  ['IA', 'Iowa'],
  ['KS', 'Kansas'],
  ['KY', 'Kentucky'],
  ['LA', 'Louisiana'],
  ['ME', 'Maine'],
  ['MD', 'Maryland'],
  ['MA', 'Massachusetts'],
  ['MI', 'Michigan'],
  ['MN', 'Minnesota'],
  ['MS', 'Mississippi'],
  ['MO', 'Missouri'],
  ['MT', 'Montana'],
  ['NE', 'Nebraska'],
  ['NV', 'Nevada'],
  ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'],
  ['NY', 'New York'],
  ['NC', 'North Carolina'],
  ['ND', 'North Dakota'],
  ['OH', 'Ohio'],
  ['OK', 'Oklahoma'],
  ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'],
  ['SD', 'South Dakota'],
  ['TN', 'Tennessee'],
  ['TX', 'Texas'],
  ['UT', 'Utah'],
  ['VT', 'Vermont'],
  ['VA', 'Virginia'],
  ['WA', 'Washington'],
  ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'],
  ['WY', 'Wyoming'],
].map(([value, label]) => ({ value, label }));

const CONDITION_FIELD_SELECT_OPTIONS: Record<string, { value: string; label: string }[]> = {
  // V1 legacy field paths
  'patient.active': BOOLEAN_OPTIONS,
  'patient.email.exists': BOOLEAN_OPTIONS,
  'patient.communicationPreference.emailAllowed': BOOLEAN_OPTIONS,
  'derived.hasValidRecipientEmail': BOOLEAN_OPTIONS,
  'derived.wasPreviouslySent': BOOLEAN_OPTIONS,
  'derived.wasPreviouslyAssigned': BOOLEAN_OPTIONS,
  'intake.status': INTAKE_STATUS_OPTIONS,
  'patient.address.state': US_STATE_OPTIONS,
  'location.address.state': US_STATE_OPTIONS,

  // V2 field paths with enum options
  'status': INTAKE_STATUS_OPTIONS,
  'episode.status': EPISODE_STATUS_OPTIONS,
  'serviceRequest.status': SERVICE_REQUEST_STATUS_OPTIONS,
  'task.status': TASK_STATUS_OPTIONS,
  'task.code': TASK_CODE_OPTIONS,
  'communication.status': COMM_STATUS_OPTIONS,
  'communication.medium': COMM_MEDIUM_OPTIONS,
  'activityDefinition.status': AD_STATUS_OPTIONS,
  'questionnaire.status': AD_STATUS_OPTIONS,
  'episode.type': EPISODE_TYPE_OPTIONS,
  'appointment.status': [
    { value: 'proposed', label: 'Proposed' },
    { value: 'booked', label: 'Booked' },
    { value: 'fulfilled', label: 'Fulfilled' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'noshow', label: 'No show' },
  ],
  'appointment.category': [
    { value: 'session', label: 'Session' },
    { value: 'group', label: 'Group' },
    { value: 'meeting', label: 'Meeting' },
    { value: 'time-entry', label: 'Time entry' },
  ],
  'appointment.modality': [
    { value: 'ROUTINE', label: 'In-person' },
    { value: 'VR', label: 'Telehealth / virtual' },
  ],
  'appointment.hasPatient': BOOLEAN_OPTIONS,
  'appointment.hasVideoLink': BOOLEAN_OPTIONS,
  'appointment.detailsChanged': BOOLEAN_OPTIONS,
  'appointment.recurringPattern': [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Biweekly' },
    { value: 'monthly', label: 'Monthly' },
  ],
  'encounter.status': [
    { value: 'planned', label: 'Planned' },
    { value: 'arrived', label: 'Arrived' },
    { value: 'in-progress', label: 'In progress' },
    { value: 'finished', label: 'Finished' },
    { value: 'cancelled', label: 'Cancelled' },
  ],
};

/**
 * Returns select options for this field/operator, or null if the value should stay free-text.
 */
export function getConditionValueSelectOptions(
  field: string,
  operator: ConditionOperator,
): { value: string; label: string }[] | null {
  if (operator === 'exists' || operator === 'not-exists') return null;
  if (operator === 'in-list' || operator === 'not-in-list') return null;

  const opts = CONDITION_FIELD_SELECT_OPTIONS[field];
  if (opts && opts.length > 0) return opts;

  // Fallback: check the field registry for enumOptions
  const registryEntry = RESOURCE_FIELD_REGISTRY.find((f) => f.fieldPath === field);
  if (registryEntry?.enumOptions && registryEntry.enumOptions.length > 0) {
    return registryEntry.enumOptions;
  }

  return null;
}

/**
 * When the field changes, drop the value if it is not valid for the new field's dropdown.
 */
export function conditionValueAfterFieldChange(
  newField: ConditionField,
  operator: ConditionOperator,
  previousValue: string,
): string {
  const opts = getConditionValueSelectOptions(newField, operator);
  if (!opts || !previousValue) return previousValue;
  return opts.some((o) => o.value === previousValue) ? previousValue : '';
}
